// Google Analytics, loaded only once the visitor has accepted the cookie
// banner.
//
// public/index.html carries no analytics tag, so this module is the only way
// gtag.js reaches the page: without a stored "granted" there is no request to
// Google and no _ga cookie. No answer and Decline are the same thing here.
//
// What is reported is also decided here, not by gtag.js. Page addresses go
// out without record ids or query strings (safePageUrl), and gtag.js is not
// allowed to add addresses it picked up from the page by itself.
import { hasAnalyticsConsent, onConsentChange } from '@/lib/consent';
import {
  analyticsCookieNames, isPrivatePath, safePageUrl, carriesRawAddress,
} from '@/lib/privacyRules';

const GA_ID = 'G-4D73K2BPC7';
const SCRIPT_ID = 'ga4-script';
// Google's own off switch: while it is true gtag.js sends nothing for this ID.
// It covers what gtag.js sends by itself (scrolls, engagement), which the
// checks in trackPageView / trackEvent below cannot reach.
const DISABLE_FLAG = `ga-disable-${GA_ID}`;

let loaded = false;      // gtag.js has been requested in this document
let active = false;      // outcome of the last sync()
let consentTold = false; // what gtag.js was last told about analytics storage
let blocked = false;     // this document has shown a private page: it sends nothing more
let reported = '';       // the page address Google was last given
let lastPage = null;     // latest page view, so Accept can count the page the visitor is on

// GA writes its cookies on the widest domain it can (".nsofar.com" from
// www.nsofar.com), so a cookie is only gone once every level has been tried.
function expireCookie(name) {
  const host = window.location.hostname;
  const labels = host.split('.');
  const scopes = ['', `;domain=${host}`];
  for (let i = 0; i < labels.length - 1; i += 1) scopes.push(`;domain=.${labels.slice(i).join('.')}`);
  scopes.forEach((scope) => {
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/${scope}`;
  });
}

// The queue between this page and gtag.js. gtag.js replaces push() with its
// own when it loads and then also uses the queue for what it notices by
// itself (see carriesRawAddress). Keeping push() behind a getter leaves this
// filter in front of whatever gtag.js installs, so those messages never
// reach it. The app reports its own page views instead, with safe addresses.
function createDataLayer() {
  const layer = [];
  let inner = Array.prototype.push;
  Object.defineProperty(layer, 'push', {
    configurable: true,
    get() {
      const target = inner;
      return function push(...messages) {
        const kept = messages.filter((message) => !carriesRawAddress(message));
        return kept.length ? target.apply(layer, kept) : true;
      };
    },
    set(replacement) { inner = replacement; },
  });
  return layer;
}

// Tells gtag.js which address to attach to everything it sends from here on,
// its own events (scrolls, engagement) included.
function reportPageAddress() {
  const here = safePageUrl(window.location.href);
  if (here === reported) return;
  window.gtag('set', { page_location: here, page_referrer: reported || safePageUrl(document.referrer) });
  reported = here;
}

function loadGoogleAnalytics() {
  loaded = true;
  consentTold = true;
  if (document.getElementById(SCRIPT_ID)) return; // dev hot reload re-runs this module
  window.dataLayer = createDataLayer();
  window.gtag = function gtag() { window.dataLayer.push(arguments); };
  // The banner asks about analytics only, so advertising signals and the
  // sending of user data stay off whatever the property's own settings are.
  window.gtag('consent', 'default', {
    analytics_storage: 'granted',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  });
  reportPageAddress();
  window.gtag('js', new Date());
  window.gtag('config', GA_ID, { send_page_view: false });
  const script = document.createElement('script');
  script.id = SCRIPT_ID;
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(script);
}

// Brings the page in line with the visitor's current choice and says whether
// analytics may run. It runs before anything is sent, so a choice changed in
// another tab is picked up as well.
function sync() {
  const consented = hasAnalyticsConsent();
  if (isPrivatePath(window.location.pathname)) blocked = true;
  const allowed = consented && !blocked;
  window[DISABLE_FLAG] = !allowed;
  if (allowed && !loaded) {
    loadGoogleAnalytics();
  } else if (loaded && consented !== consentTold) {
    // A change of mind while gtag.js is already on the page.
    window.gtag('consent', 'update', { analytics_storage: consented ? 'granted' : 'denied' });
    consentTold = consented;
  }
  if (allowed) reportPageAddress();
  if (!consented) analyticsCookieNames(document.cookie).forEach(expireCookie);
  active = allowed;
  return allowed;
}

// The in-house click recorder kept the signed-in user's email in
// localStorage and never removed it. Nothing loads the recorder any more,
// so drop that copy whenever someone comes back.
function clearRecorderLeftover() {
  try {
    window.localStorage.removeItem('nsofar_user_email');
  } catch {
    // Storage is blocked in this browser: there is nothing to clear.
  }
}

function sendPageView(page) {
  window.gtag('event', 'page_view', { page_title: page.title });
}

export function trackPageView(title) {
  lastPage = { title };
  if (sync()) sendPageView(lastPage);
}

export function trackEvent(name, params = {}) {
  if (sync()) window.gtag('event', name, params);
}

// Accept counts the page the visitor is already on. Decline after Accept
// stops Google Analytics and removes its cookies; sync() does that half.
onConsentChange(() => {
  const wasActive = active;
  if (sync() && !wasActive && lastPage) sendPageView(lastPage);
});

// Startup: a stored "granted" loads analytics before the first page view is
// reported; anything else leaves the page clean of old analytics cookies.
clearRecorderLeftover();
sync();
